import { useCallback, useEffect, useState } from 'react';

/**
 * Captura o evento `beforeinstallprompt` (Chrome/Edge) e expõe a ação
 * `install()` que dispara o prompt nativo. iOS Safari não emite o evento;
 * `canInstall` continua false e o hook é silencioso.
 *
 * Estado dispensado é persistido por 7 dias para não pestilenciar o usuário.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'rx-install-prompt-dismissed-at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1_000;

function isRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number.parseInt(raw, 10);
    if (!Number.isFinite(dismissedAt)) return false;
    return Date.now() - dismissedAt < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // Safari/iOS PWA exposes navigator.standalone
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

interface UseInstallPromptValue {
  canInstall: boolean;
  isStandalone: boolean;
  install(): Promise<'accepted' | 'dismissed' | 'unavailable'>;
  dismiss(): void;
}

export function useInstallPrompt(): UseInstallPromptValue {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [standalone, setStandalone] = useState<boolean>(isStandalone());
  const [dismissed, setDismissed] = useState<boolean>(isRecentlyDismissed());

  useEffect(() => {
    function handleBeforeInstall(rawEvent: Event): void {
      rawEvent.preventDefault();
      setEvent(rawEvent as BeforeInstallPromptEvent);
    }
    function handleInstalled(): void {
      setEvent(null);
      setStandalone(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const install = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!event) return 'unavailable';
    await event.prompt();
    const result = await event.userChoice;
    setEvent(null);
    if (result.outcome === 'dismissed') {
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {
        /* tolerante */
      }
      setDismissed(true);
    }
    return result.outcome;
  }, [event]);

  const dismiss = useCallback((): void => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* tolerante */
    }
    setDismissed(true);
  }, []);

  return {
    canInstall: Boolean(event) && !standalone && !dismissed,
    isStandalone: standalone,
    install,
    dismiss,
  };
}
